import { Box, Heading, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  Button,
  Link,
  LoadingIndicator,
  StatusWithText,
  useToast,
} from "../../../components";
import config from "../../../config";
import {
  CurrentUserFragment,
  InstallZoomNotetakerAppJobProcessingStatus,
  useAuthorizeZoomBotMutation,
  useInstallZoomNotetakerAppStatusQuery,
} from "../../graphql";
import StyledZoomImg from "./StyleZoomImg";

interface ZoomBotRecordingAuthProps {
  currentUser: CurrentUserFragment;
}

const ZoomBotRecordingAuth: React.FC<ZoomBotRecordingAuthProps> = ({
  currentUser,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authCode = searchParams.get("code");
  const toast = useToast();

  const [authorizeZoomBotMutation, { loading: loadingAuthorize }] =
    useAuthorizeZoomBotMutation({
      onError: () => {
        toast({
          status: "error",
          title: "Authorizing Zoom",
          description: "There was a problem - please try again",
        });
      },
    });

  const appStatusData = useInstallZoomNotetakerAppStatusQuery();

  useEffect(() => {
    if (currentUser.userRole?.canManageIntegrationSettings) {
      if (authCode) {
        authorizeZoomBotMutation({
          variables: { authCode },
          onCompleted: () => {
            appStatusData.refetch();
            appStatusData.startPolling(1500);
          },
        });
        navigate(location.pathname, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (
      appStatusData?.data?.zoomBotInstallStatus &&
      [
        InstallZoomNotetakerAppJobProcessingStatus.Failed,
        InstallZoomNotetakerAppJobProcessingStatus.Completed,
      ].includes(appStatusData?.data?.zoomBotInstallStatus)
    ) {
      appStatusData.stopPolling();
    }
    if (
      appStatusData?.data?.zoomBotInstallStatus &&
      [
        InstallZoomNotetakerAppJobProcessingStatus.Dispatched,
        InstallZoomNotetakerAppJobProcessingStatus.InProgress,
      ].includes(appStatusData?.data?.zoomBotInstallStatus)
    ) {
      appStatusData.startPolling(1500);
    }
  }, [appStatusData.data]);

  if (
    loadingAuthorize ||
    appStatusData.loading ||
    (appStatusData?.data?.zoomBotInstallStatus &&
      [
        InstallZoomNotetakerAppJobProcessingStatus.Dispatched,
        InstallZoomNotetakerAppJobProcessingStatus.InProgress,
      ].includes(appStatusData?.data?.zoomBotInstallStatus))
  ) {
    return <LoadingIndicator />;
  }

  if (
    appStatusData?.data?.zoomBotInstallStatus ===
    InstallZoomNotetakerAppJobProcessingStatus.Completed
  ) {
    return (
      <>
        <Heading as="h4" fontSize="md" mb={8}>
          Setup status
        </Heading>
        <ZoomSettingsBox />
        <Box display="flex" alignItems="center" my={2}>
          <StatusWithText my={5} passed label="Zoom App installed" />
          {config.zoomBotClientId && (
            <Button
              as="a"
              href={`https://zoom.us/oauth/authorize?response_type=code&client_id=${config.zoomBotClientId}&redirect_uri=${config.urlPrefix}/settings/zoom-notetaker`}
              target="_blank"
              rel="noopener noreferrer"
              colorScheme="blue"
              variant="solid"
              ml={6}
              borderRadius="full"
            >
              Reinstall
            </Button>
          )}
        </Box>
      </>
    );
  }

  if (config.zoomBotClientId) {
    return (
      <>
        <Heading as="h4" fontSize="md" mb={8}>
          Setup instructions
        </Heading>
        <ZoomSettingsBox />
        <Box display="flex" alignItems="center" my={2}>
          <StatusWithText
            my={5}
            mr={6}
            passed={false}
            label="Zoom App not installed"
          />
          <a
            href={`https://zoom.us/oauth/authorize?response_type=code&client_id=${config.zoomBotClientId}&redirect_uri=${config.urlPrefix}/settings/zoom-notetaker`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <StyledZoomImg
              src="https://marketplacecontent.zoom.us/zoom_marketplace/img/add_to_zoom.png"
              alt="Add to ZOOM"
            />
          </a>
        </Box>
      </>
    );
  }

  return null;
};

const ZoomSettingsBox: React.FC = () => {
  return (
    <Box
      bg="blue.50"
      p={4}
      mb={4}
      mt={4}
      borderRadius="md"
      borderLeft="4px solid blue"
    >
      <Heading as="h4" size="sm" mb={2} display="flex" alignItems="center">
        <FaInfoCircle color="blue" style={{ marginRight: "8px" }} />
        Setup instructions
      </Heading>
      <Text fontSize="sm">
        You must configure{" "}
        <Link
          href="https://us02web.zoom.us/profile/setting?ampDeviceId=59fd0ddc-01d1-425f-9e64-430be72b75d5&tab=recording"
          target="_blank"
        >
          these Zoom settings
        </Link>{" "}
        for the Zoom recording disclaimer dialog box to work:
      </Text>
      <UnorderedList fontSize="sm" mt={2}>
        <ListItem>
          Record to computer: <strong>enabled</strong>
        </ListItem>
        <ListItem>
          External Meeting participants can ask for permission to record:{" "}
          <strong>enabled</strong>
        </ListItem>
      </UnorderedList>
      <Text fontSize="sm" mt={2}>
        If you do not want to configure these settings, there are alternative
        options, but you will need to reach out to support or your CSM.
      </Text>
    </Box>
  );
};

export default ZoomBotRecordingAuth;
